import React, { Fragment, useRef, useState, useEffect } from 'react';

import STableLayout, { TableLayoutRef } from "@/styles/STableLayout";
import DetailModal from '@/components/DetailModal';
import { useTranslation } from 'react-i18next';
import { Spin, App, InputNumber, Button, Tooltip, DatePicker, Flex, Input } from 'antd';
import { DeleteOutlined, PlusOutlined, BarcodeOutlined } from '@ant-design/icons';
import { InboundContentWrap } from './style';
import { orderFetch, addItemlocation, itemLocationRms, orderConfirm, orderArrival, orderDescUpdate } from '@/apis/inventoryv2';
import { OrderV2, OrderItemV2, WLocation, AddItemLocation } from '@/types/inventoryV2';
import { TableProps } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { AnyObject } from 'antd/lib/_util/type';
import SText from '@/styles/SText';
import { LocationSelect } from '@/components/Selector';
import BarcodeEditModal from './BarcodeEditModal';
import dayjs, { Dayjs } from 'dayjs';
import { getLocationIdx } from '@/utils/wLocation';
import OrderConfirmInspection from '@/components/OrderConfirmInspection';
import { SpecialHandlingConfirm } from '@/types/inventoryV2';

interface InboundOrderFormProps {
  sourceId: string;
  onCancel: () => void
}

interface InboundLocation {
  itemLocId: string;
  orderItemId: string;
  location: WLocation;
  count: number;
  barcodes: Array<string>
}

type InbountItems = OrderItemV2 & {
  barcodeLocations?: Array<InboundLocation>
}

const InboundOrderForm: React.FC<InboundOrderFormProps> = ({ sourceId, onCancel }) => {
  const { t } = useTranslation();
  const { notification, modal } = App.useApp();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [hasModified, setHasModified] = useState<boolean>(false);
  const [orderData, setOrderData] = useState<OrderV2 | null>(null);
  const [dataSource, setDataSource] = useState<InbountItems[]>([]);
  const [dataSourceTemp, setDataSourceTemp] = useState<InbountItems[]>([]);
  const [currentEditingItemId, setCurrentEditingItemId] = useState<string | null>(null);
  const [arrivalTime, setArrivalTime] = useState<Dayjs>(dayjs());
  const [remark, setRemark] = useState<string>('');

  const [changingLocation, setChangingLocation] = useState<{
    itemId: string | null;
    locationId: string | null;
  }>({ itemId: null, locationId: null });
  const [barcodeModalInfo, setBarcodeModalInfo] = useState<{
    open: boolean;
    itemId: string | null;
    locationId: string | null;
    barcodes: string[];
  }>({ open: false, itemId: null, locationId: null, barcodes: [] });
  const [openInspection, setOpenInspection] = useState<boolean>(false);
  const [inspectionItems, setInspectionItems] = useState<InbountItems[]>([]);

  useEffect(() => {
    if (sourceId) {
      fetchOrderData();
    }
  }, [sourceId]);

  const fetchOrderData = async () => {
    setLoading(true);
    try {
      const data = await orderFetch(sourceId);
      data.arrivalAt && setArrivalTime(dayjs(data.arrivalAt))
      setOrderData(data);
      setRemark(data.desc)
      if (data.items?.length > 0) {
        const items = data.items.map(itm => (
          {
            ...itm,
            barcodeLocations: itm.locations?.length > 0 ?
              itm.locations.map(loc => ({
                itemLocId: loc.id,
                orderItemId: itm.id,
                location: loc.location,
                count: loc.count,
                barcodes: loc.barcodes?.length > 0 ?
                  loc.barcodes.map(bc => bc.barcode)
                  : []
              }))
              : []
          }
        ))
        setDataSource(items)
        setDataSourceTemp(items)
        setHasModified(false);
      }
    } catch (error) {
      console.error('Failed to fetch order data:', error);
      notification.error({ placement: 'top', message: t('warehouse.fetchError') });
    } finally {
      setLoading(false);
    }
  };

  // 处理库位数量变更
  const handleLocationCountChange = (
    value: number | null,
    recordId: string,
    locationId: string,
    currentBarcodes: string[],
  ) => {
    // 更新库位数量
    if (value !== null && value !== undefined) {
      // 检查当前条码数量
      if (currentBarcodes.length > value) {
        // 如果新数量小于条码数量，提示用户检查条码
        notification.warning({
          placement: 'top',
          message: t('warehouse.checkBarcodes'),
          description: t('warehouse.barcodesExceedNewCount')
        });
        // 不做数据更改，保持原值
        return;
      }

      // 只有条码数量不超过新数量时才更新
      setDataSource(prev => prev.map(item => {
        if (item.id === recordId) {
          return {
            ...item,
            barcodeLocations: item.barcodeLocations?.map(bLoc => {
              if (bLoc.location.id === locationId) {
                return {
                  ...bLoc,
                  count: value
                };
              }
              return bLoc;
            })
          };
        }
        return item;
      }));
      setHasModified(true);
    }
  };

  // 处理库位选择变更
  const handleLocationSelectChange = (location: WLocation) => {
    // 处理更换库位的逻辑
    if (changingLocation.itemId && changingLocation.locationId) {
      // 检查新库位是否已存在
      const currentItem = dataSource.find(item => item.id === changingLocation.itemId);
      const isLocationExist = currentItem?.barcodeLocations?.some(
        loc => loc.location.id === location.id
      );

      if (isLocationExist) {
        notification.warning({
          placement: 'top',
          message: t('warehouse.locationExist')
        });
        return;
      }

      // 更换库位
      setDataSource(prev => prev.map(item => {
        if (item.id === changingLocation.itemId) {
          return {
            ...item,
            barcodeLocations: item.barcodeLocations?.map(bLoc => {
              if (bLoc.location.id === changingLocation.locationId) {
                return {
                  ...bLoc,
                  location: location
                };
              }
              return bLoc;
            })
          };
        }
        return item;
      }));
      setChangingLocation({ itemId: null, locationId: null });
      setHasModified(true);
      return;
    }

    // 处理添加新库位的逻辑
    if (currentEditingItemId && location) {
      // 检查该库位是否已存在
      const currentItem = dataSource.find(item => item.id === currentEditingItemId);
      const isLocationExist = currentItem?.barcodeLocations?.some(
        loc => loc.location.id === location.id
      );

      if (isLocationExist) {
        notification.warning({
          placement: 'top',
          message: t('warehouse.locationExist')
        });
        return;
      }

      // 检查添加新库位后总数量是否会超过订单项的count
      const currentTotalSum = currentItem?.barcodeLocations
        ?.reduce((sum, bLoc) => sum + bLoc.count, 0) || 0;

      if (currentItem && currentTotalSum >= currentItem.count) {
        notification.warning({
          placement: 'top',
          message: t('warehouse.cannotAddLocation'),
          description: t('warehouse.totalReachedItemCount', { total: currentItem.count })
        });
        return;
      }

      setDataSource(prev => prev.map(item => {
        if (item.id === currentEditingItemId) {
          const newBarcodeLocation: InboundLocation = {
            itemLocId: '',
            orderItemId: currentEditingItemId,
            location: location,
            count: 0,
            barcodes: []
          };
          return {
            ...item,
            barcodeLocations: [...(item.barcodeLocations || []), newBarcodeLocation]
          };
        }
        return item;
      }));
      setCurrentEditingItemId(null);
      setHasModified(true);
    }
  };

  // 处理条码编辑确认
  const handleBarcodeEditOk = (barcodes: string[]) => {
    if (barcodeModalInfo.itemId && barcodeModalInfo.locationId) {
      // 找到当前库位的 count 值
      const currentItem = dataSource.find(item => item.id === barcodeModalInfo.itemId);
      const currentLocation = currentItem?.barcodeLocations?.find(
        loc => loc.location.id === barcodeModalInfo.locationId
      );

      let finalBarcodes = barcodes;

      // 如果条码数量超过 count，自动截取并提示
      if (currentLocation && barcodes.length > currentLocation.count) {
        finalBarcodes = barcodes.slice(0, currentLocation.count);
        notification.warning({
          placement: 'top',
          message: t('warehouse.barcodesAutoTrimmed'),
          description: t('warehouse.barcodesAutoTrimmedDesc', { count: currentLocation.count })
        });
      }

      setDataSource(prev => prev.map(item => {
        if (item.id === barcodeModalInfo.itemId) {
          return {
            ...item,
            barcodeLocations: item.barcodeLocations?.map(loc => {
              if (loc.location.id === barcodeModalInfo.locationId) {
                return {
                  ...loc,
                  barcodes: finalBarcodes
                };
              }
              return loc;
            })
          };
        }
        return item;
      }));
      setHasModified(true);
    }
    setBarcodeModalInfo({ open: false, itemId: null, locationId: null, barcodes: [] });
  };

  const orderConfirmFunc = async (specialHandling: SpecialHandlingConfirm[]) => {
    try {
      await orderArrival(sourceId, arrivalTime?.endOf('day')?.valueOf());
      await orderDescUpdate(sourceId, remark)
      await orderConfirm(sourceId, 0, specialHandling);
      // try {
      //   await orderConfirm(sourceId, 0, specialHandling);
      // } catch (error: any) {
      //   if (error?.code === 70845) await orderConfirm(sourceId, 0);
      //   else throw error
      // }
      notification.success({ placement: 'top', message: t('warehouse.inboundSuccess') });
      onCancel(); // 成功后关闭弹窗
    } catch (error: any) {
      // 库位添加成功但订单状态更新失败
      console.error('Failed to update order status:', error);

      // 根据错误码进行不同的处理
      if (error?.code === 73011) {
        let ids: string[] = (error?.extra ?? []);
        let codes = (orderData?.items ?? []).filter(item => ids.includes(item.productId)).map(p => p.product.code).join(', ')
        notification.error({
          message: t('warehouse.barcodeErr'),
          description: codes
        });
        notification.error({
          placement: 'top',
          message: t('warehouse.inboundFailed'),
          description: t('warehouse.inventoryMismatch')
        });
      } else if (error === 72811) {
        console.log(">>>>待捕获");
        notification.error({
          placement: 'top',
          message: t('warehouse.inboundFailed'),
          description: t('product.dataError')
        });
      } else if (error === 73010) {
        // 其他错误
        notification.error({
          placement: 'top',
          message: t('warehouse.inboundFailed'),
          description: t('warehouse.locFailed'),
        });
      } else {
        // 其他错误
        notification.error({
          placement: 'top',
          message: t('warehouse.inboundFailed'),
          description: error
        });
      }
    } finally {
      setConfirmLoading(false);
    }
  }

  // 确认入库的处理函数
  const handleConfirm = async () => {

    // 验证每个订单项的库位数量总和
    const invalidItems: string[] = [];

    for (const item of dataSource) {
      const locationSum = item.barcodeLocations
        ?.reduce((sum, loc) => sum + loc.count, 0) || 0;

      if (locationSum !== item.count) {
        invalidItems.push(
          `${item.product?.code || item.id}: ${t('warehouse.currentTotal')} ${locationSum}, ${t('warehouse.requiredTotal')} ${item.count}`
        );
      }
    }

    if (invalidItems.length > 0) {
      notification.error({
        placement: 'top',
        message: t('warehouse.quantityMismatch'),
        description: (
          <div>
            <div>{t('warehouse.quantityMismatchDesc')}</div>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              {invalidItems.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        ),
        duration: 3,
      });
      return;
    }
    // 执行入库
    setConfirmLoading(true);
    if (!hasModified) setInspectionItems(dataSource);
    else {
      try {
        // 第一步：为每个 item 调用 itemLocationRms 删除旧的库位数据
        const rmsRequests = dataSourceTemp.map(item => {
          // 获取该 item 的所有 itemLocId，过滤空字符串
          const itemLocIds = item.barcodeLocations
            ?.map(loc => loc.itemLocId) || []

          // 只有当有 itemLocId 时才调用删除接口
          if (itemLocIds.length > 0) {
            return itemLocationRms(item.id, itemLocIds);
          }
          return Promise.resolve(null);
        });

        // 执行所有删除操作
        const rmsResults = await Promise.allSettled(rmsRequests);

        // 检查删除操作是否有失败
        const rmsFailedResults = rmsResults.filter(r => r.status === 'rejected');
        if (rmsFailedResults.length > 0) {
          console.error('Failed to remove old locations:', rmsFailedResults);
          notification.error({
            placement: 'top',
            message: t('warehouse.removeOldLocationsFailed'),
            description: t('common.pleaseTryAgain')
          });
          setConfirmLoading(false);
          return;
        }

        // 第二步：处理每个 item 的 addItemLocation 请求
        const addRequests = dataSource.map((item, index) => {
          // 准备 AddItemLocation 数据
          const locations: AddItemLocation[] = item.barcodeLocations?.map(loc => ({
            locationId: loc.location.id,
            count: loc.count,
            barcodes: loc.barcodes
          })) || [];

          // 只有当 locations 不为空时才发送请求
          if (locations.length > 0) {
            return addItemlocation(item.id, locations).then(result => ({
              index,
              itemId: item.id,
              result
            }));
          }
          return Promise.resolve(null);
        });

        // 使用 allSettled 并发执行所有请求，即使部分失败也会继续
        const results = await Promise.allSettled(addRequests);

        // 处理成功的结果，更新 dataSourceTemp 中的 itemLocId
        const successfulUpdates: InbountItems[] = [...dataSourceTemp];
        let hasUpdates = false;

        results.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value) {
            const { itemId, result: newLocations } = result.value;
            // 找到对应的 item 在 successfulUpdates 中的索引
            const itemIndex = successfulUpdates.findIndex(item => item.id === itemId);
            if (itemIndex !== -1 && newLocations) {
              // 更新该 item 的 barcodeLocations，添加新的 itemLocId
              successfulUpdates[itemIndex] = {
                ...successfulUpdates[itemIndex],
                locations: [...newLocations],
                barcodeLocations: newLocations.map(newLoc => ({
                  itemLocId: newLoc.id,  // 使用返回的新 ID
                  orderItemId: itemId,
                  location: newLoc.location,
                  count: newLoc.count,
                  barcodes: newLoc.barcodes?.map(bc => bc.barcode) || []
                }))
              };
              hasUpdates = true;
            }
          }
        });

        // 如果有更新，保存到 dataSourceTemp
        if (hasUpdates) {
          setDataSourceTemp(successfulUpdates);
          setInspectionItems(successfulUpdates);
        }

        // 分析结果
        const successResults = results.filter(r => r.status === 'fulfilled' && r.value !== null);
        const failedResults = results.filter(r => r.status === 'rejected');

        if (failedResults.length > 0) {
          // 部分失败
          const failedItems = failedResults.map((r) => {
            const itemIndex = results.indexOf(r);
            const item = dataSource[itemIndex];
            return item?.product?.name || `Item ${itemIndex + 1}`;
          });

          notification.error({
            placement: 'top',
            message: t('warehouse.someItemsInboundFailed'),
            description: (
              <div>
                <div>{t('warehouse.inboundResultDetail', {
                  success: successResults.length,
                  failed: failedResults.length
                })}</div>
                <div style={{ marginTop: 8 }}>{t('warehouse.failedItems')}：{failedItems.join('、')}</div>
              </div>
            ),
            duration: 5
          });
          return setConfirmLoading(false);
        } else {
          setHasModified(false);
          setDataSource(successfulUpdates);
        }
      } catch (error) {
        console.error('Inbound failed:', error);
        setConfirmLoading(false);
        notification.error({
          placement: 'top',
          message: t('warehouse.inboundError'),
          description: error instanceof Error ? error.message : String(error)
        });
      }
    }
    setConfirmLoading(false);
    setOpenInspection(true);
  };

  const doConfirm = async (specialHandling: SpecialHandlingConfirm[]) => {
    setConfirmLoading(true);
    try {
      await orderConfirmFunc(specialHandling);
    } catch (error) {
      notification.error({
        placement: 'top',
        message: t('warehouse.inboundError'),
      });
    }
    setConfirmLoading(false);

  };

  const columns: TableProps<InbountItems>['columns'] = [
    {
      title: t('warehouse.batchNo'),
      key: 'lot',
      width: 150,
      fixed: 'left',
      render: (_, record) => <SText text={record.lot || ''} />
    },
    {
      title: t('warehouse.supplierBatchNo'),
      key: 'thdLot',
      width: 150,
      render: (_, record) => <SText text={record.thdLot || ''} />
    },
    {
      title: t('purchase.productInfo'),
      children: [
        {
          title: <SText text={t('product.code')} />,
          key: 'productCode',
          width: 120,
          render: (_, record) => <SText text={record.product?.code || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.name')} />,
          key: 'productName',
          width: 150,
          render: (_, record) => <SText text={record.product?.name || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.brand1')} />,
          key: 'model',
          width: 120,
          render: (_, record) => <SText text={record.product?.model || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.uom')} />,
          key: 'uom',
          width: 80,
          render: (_, record) => <SText text={record.product?.uom || ''} style={{ width: "100%" }} />
        },
      ]
    },
    {
      title: t('order.quantity'),
      dataIndex: 'count',
      key: 'count',
      width: 100,
      fixed: 'right',
      render: (value) => value || 0
    },
    {
      title: t('warehouse.location'),
      key: 'barcodeLocations',
      width: 380,
      fixed: "right",
      render: (_, record) => {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {record.barcodeLocations?.map((loc) => (
              <div key={`${record?.id}-${loc?.location?.id}`} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <InputNumber changeOnWheel
                  addonBefore={
                    <Tooltip title={t('warehouse.changeLocation')}>
                      <div
                        style={{
                          cursor: 'pointer',
                          userSelect: 'none',
                          display: 'inline-block',
                          width: '160px',
                          wordBreak: "break-all",
                          whiteSpace: "normal"
                        }}
                        onClick={() => {
                          setChangingLocation({
                            itemId: record.id,
                            locationId: loc.location.id
                          });
                        }}
                      >
                        {getLocationIdx(loc.location)}
                      </div>
                    </Tooltip>
                  }
                  addonAfter={
                    <Tooltip title={t('warehouse.barcodeCount')}>
                      <span>{loc.barcodes.length}</span>
                    </Tooltip>
                  }
                  controls={false}
                  value={loc.count}
                  min={0}
                  max={record.count - (record.barcodeLocations?.filter(l => l?.location?.id !== loc?.location?.id)?.reduce((sum, bLoc) => sum + bLoc.count, 0) || 0)}
                  style={{ height: "100%" }}
                  onChange={(value) => handleLocationCountChange(
                    value,
                    record.id,
                    loc.location.id,
                    loc.barcodes,
                  )}
                />
                <Tooltip title={t('warehouse.editBarcodes')}>
                  <Button
                    type="text"
                    icon={<BarcodeOutlined />}
                    size="small"
                    onClick={() => {
                      setBarcodeModalInfo({
                        open: true,
                        itemId: record.id,
                        locationId: loc.location.id,
                        barcodes: loc.barcodes || []
                      });
                    }}
                  />
                </Tooltip>
                <Tooltip title={t('common.del')}>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    onClick={() => {
                      // 删除 barcodeLocations 中的数据
                      setDataSource(prev => prev.map(item => {
                        if (item.id === record.id) {
                          return {
                            ...item,
                            barcodeLocations: item.barcodeLocations?.filter(
                              bLoc => bLoc.location.id !== loc.location.id
                            )
                          };
                        }
                        return item;
                      }));
                      setHasModified(true);
                    }}
                  />
                </Tooltip>
              </div>
            ))}
            <Tooltip title={t('warehouse.addLocation')}>
              <Button disabled={(record.barcodeLocations?.reduce((sum, bLoc) => sum + bLoc.count, 0) || 0) >= record.count}
                type="dashed"
                icon={<PlusOutlined />}
                size="small"
                style={{ width: '100%' }}
                onClick={() => {
                  setCurrentEditingItemId(record.id);
                }}
              >
                {t('common.add')}
              </Button>
            </Tooltip>
          </div>
        );
      }
    },
  ];

  return (
    <DetailModal noLog
      modalProps={{
        width: 1200,
        title: `${t('warehouse.inbound')}  ${orderData?.number || ''}`,
        onCancel,
        footer: (
          <div style={{ padding: '10px 16px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button key="cancel" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
            <Button key="submit" type="primary" onClick={handleConfirm} loading={confirmLoading}>
              {t('common.confirm')}
            </Button>
          </div>
        )
      }}
    >
      <InboundContentWrap>
        <Flex vertical gap={16} style={{ width: "100%", marginBottom: 16, marginTop: -6 }}>
          <Flex gap={8} align='center'>
            <span style={{ width: '80px' }}>{t('warehouse.arrivalAt')}：</span>
            <DatePicker style={{ width: "240px" }} allowClear={false}
              placeholder={t("warehouse.plArrivalAt")}
              value={arrivalTime}
              onChange={setArrivalTime}
              disabled={!!orderData?.arrivalAt}
            />
          </Flex>
          <Flex gap={8} align='center' style={{ flex: 1 }}>
            <span style={{ width: '80px', whiteSpace: 'nowrap' }}>{t('warehouse.remark')}：</span>
            <Input.TextArea
              style={{ width: "100%" }}
              autoSize={{ minRows: 2, maxRows: 4 }}
              value={remark}
              onChange={e => setRemark(e.target.value)}
              maxLength={500}
              showCount
              placeholder={t('warehouse.enterRemark')}
            />
          </Flex>
        </Flex>
        {
          loading ? <div className="loading-box"><Spin /></div>
            : <STableLayout
              headerChildren={null}
              total={dataSource.length}
              tableLayoutRef={tableRef}
              columns={columns as ColumnsType<AnyObject>}
              dataSource={dataSource}
              rowKey="id"
              pagination={false}
            />
        }

      </InboundContentWrap>

      <LocationSelect
        open={!!currentEditingItemId || !!changingLocation.itemId}
        value={(() => {
          // 如果是更换库位，传递当前库位的ID作为默认选中
          if (changingLocation.itemId && changingLocation.locationId) {
            return changingLocation.locationId;
          }
          return undefined;
        })()}
        onChange={handleLocationSelectChange}
        onCancel={() => {
          setCurrentEditingItemId(null);
          setChangingLocation({ itemId: null, locationId: null });
        }}
      />

      <BarcodeEditModal
        open={barcodeModalInfo.open}
        initialBarcodes={barcodeModalInfo.barcodes}
        onOk={handleBarcodeEditOk}
        onCancel={() => {
          setBarcodeModalInfo({ open: false, itemId: null, locationId: null, barcodes: [] });
        }}
      />
      {/* 巡检弹窗：onOk 后执行入库确认逻辑 */}
      <OrderConfirmInspection
        items={inspectionItems}
        open={openInspection}
        onOk={(data) => {
          setOpenInspection(false);
          doConfirm(data);
        }}
        onClose={() => setOpenInspection(false)}
      />
    </DetailModal>
  )
};
export default InboundOrderForm;